/// <amd-module name="Wiki/Renderer/MarkdownSyntaxExtensionProvider" />
import * as MarkdownIt from "markdown-it";
import { newGuid } from "VSS/Core/Util/String";
import { MarkdownConstants } from "VSS/Features/Markdown/MarkdownConstants";
import { ContainerOptions } from "VSS/Features/Markdown/MarkdownRendererContracts";
import { ILegacyPlatformService } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { ClientContribution as Contribution } from "VSS/Platform/Contracts/Contributions";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssLocationService } from "VSS/Platform/Location";
import { combineUrlPaths } from "VSS/Platform/Util/Url";

export interface PreviewModeMetaData {
    getViewMode(): number;
}

export namespace PreviewMode {
    export const Off = 1;
    export const On = 2;
}

interface MarkdownExtenstionContributionInstance {
    sendContent(content: string, isEditMode: boolean, pageContext: IVssPageContext): void;
}

interface ContributionInstanceObject {
    data?: string;
    instance?: MarkdownExtenstionContributionInstance;
}

const enum ContributionKeys {
    MarkdownSyntaxContribution = "ms.vss-tfs-web.markdown-extension-syntax",
    MarkdownSyntaxContributionType = "ms.vss-tfs-web.markdown-extension"
}

// This class uses wiki-extension contributions to render extensions
export class MarkdownSyntaxExtensionProvider implements ContainerOptions {
    private _currentElementId: string;
    private _currentTokenIndex: number;
    private _elementInstanceMap: Map<string, ContributionInstanceObject> = new Map<string, ContributionInstanceObject>();
    private _keywordContributionMap: Map<string, Contribution> = new Map<string, Contribution>();
    private _seenKeywordsSet: Set<string> = new Set<string>();
    private _curentKeyWord: string;
    private _contributedControlHelperModulePromise: Promise<any>;

    public name = "markdownExtension";

    constructor(private _pageContext: IVssPageContext, private _viewModeObj: PreviewModeMetaData, private _className: string) {}

    public createContributionsHosts = (): void => {
        this._seenKeywordsSet.forEach(this._createContributionHosts);
    };

    // if we want some extensions to be applied  only for specific hubs, target those extensions to @additionalSyntaxContributorsTargetId and pass that id here.
    // e.g: query-table extension to be rendered only from wiki-hub.
    public fetchMarkdownSyntaxContributors = (additionalSyntaxContributorsTargetId?: string): Contribution[] => {
        let contributions = _getContributionsForTarget(
            this._pageContext,
            ContributionKeys.MarkdownSyntaxContribution,
            ContributionKeys.MarkdownSyntaxContributionType
        );

        if (additionalSyntaxContributorsTargetId) {
            const additionalContributions = _getContributionsForTarget(
                this._pageContext,
                additionalSyntaxContributorsTargetId,
                ContributionKeys.MarkdownSyntaxContributionType
            );
            contributions = contributions.concat(additionalContributions);
        }

        this._buildContributionsMap(contributions);

        return contributions;
    };

    public render = (tokens: MarkdownIt.Token[], idx: number): string => {
        if (tokens[idx].nesting === 1) {
            this._curentKeyWord = _extractKeyWord(tokens[idx].info);
            this._seenKeywordsSet.add(this._curentKeyWord);
            this._currentElementId = newGuid();
            this._currentTokenIndex = idx;
            tokens = _modifyContainerTokens(this.name, tokens, idx);
            return `<DIV class=${this._curentKeyWord} id=${this._currentElementId}>`;
        } else {
            const extensionData = _extractContent(tokens, this._currentTokenIndex, idx);
            const currContribution = this._keywordContributionMap.get(this._curentKeyWord);
            const currentInstance = this._elementInstanceMap.get(this._currentElementId);
            if (currentInstance) {
                currentInstance.data = extensionData;
            } else {
                this._elementInstanceMap.set(this._currentElementId, { data: extensionData });
            }

            if (this._viewModeObj.getViewMode() === PreviewMode.On && currContribution && currContribution.properties.previewSrc) {
                return _getPreview(this._pageContext, currContribution.properties.previewSrc) + "</DIV>";
            }

            return "</DIV>";
        }
    };

    public validate = (params: string): boolean => {
        const keyWord = _extractKeyWord(params);
        return keyWord ? this._keywordContributionMap.has(keyWord) : false;
    };

    private _buildContributionsMap = (contributionList: Contribution[]): void => {
        contributionList.forEach((contribution: Contribution) => {
            this._keywordContributionMap.set(contribution.properties.keyword, contribution);
        });
    };

    private _createContributionHosts = (keyword: string): void => {
        const contribution = this._keywordContributionMap.get(keyword);
        const elements = document.querySelectorAll(`${this._className}` + " " + `.${keyword}`);
        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            const instanceObject = this._elementInstanceMap.get(element.id);
            this._elementInstanceMap.delete(element.id);

            if (!instanceObject || instanceObject.instance || !contribution) {
                continue;
            }
            // TODO: 1478118: Check if we can use ExternalContent component here
            this._createLegacyContributionsControl(element, contribution).then((instance: MarkdownExtenstionContributionInstance) => {
                instanceObject.instance = instance;
                instance.sendContent(instanceObject.data || "", this._viewModeObj.getViewMode() === PreviewMode.On, this._pageContext);
            });
        }
    };

    private _createLegacyContributionsControl = (element: Element, contribution: Contribution): Promise<MarkdownExtenstionContributionInstance> => {
        const legacyPlatformService = this._pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");
        if (!this._contributedControlHelperModulePromise) {
            this._contributedControlHelperModulePromise = legacyPlatformService.requireModules(["Wiki/Scripts/ContributedControlHelper"]);
        }

        return this._contributedControlHelperModulePromise.then(([contributedControlHelperModule]) => {
            return contributedControlHelperModule.createContributedControl(element, contribution);
        });
    };
}

// exported for testing
export function _getPreview(pageContext: IVssPageContext, previewSrc: string): string {
    const locationService = pageContext.getService<IVssLocationService>("IVssLocationService");
    return `<img src="${locationService.getAssetLocation(combineUrlPaths("ms.vss-wiki-web/wiki-renderer-content/images/", previewSrc))}"></img>`;
}

// exported for testing
export function _extractKeyWord(params: string): string {
    const keyWords = params.split(/(\s+)/).filter(word => word.trim().length > 0);
    return keyWords.length > 0 ? keyWords[0] : "";
}

// exported for testing
export function _removeKeyWord(params: string): string {
    const keyWords = params.split(/(\s+)/).filter(word => word.trim().length > 0);
    return keyWords.length > 0 ? keyWords.slice(1).join(" ") : "";
}

// exported for testing
export function _extractContent(tokens: MarkdownIt.Token[], startIndex: number, endIndex: number): string {
    let extensionData = _removeKeyWord(tokens[startIndex].info);
    for (let i = startIndex + 1; i < endIndex; i++) {
        // We need only include all the new line tokens except the first and last
        // which correspond to the beginning and end of the container
        if (i > startIndex + 1 && i < endIndex - 1 && tokens[i].content === "") {
            extensionData += "\n";
        } else {
            extensionData += tokens[i].content;
        }
    }
    return extensionData;
}

// exported for testing
export function _getContributionsForTarget(pageContext: IVssPageContext, contributionId: string, contributionType: string): Contribution[] {
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const childContributionIds = contributionService.getContributionChildren(contributionId);
    const childContributions: Contribution[] = [];

    if (childContributionIds && childContributionIds.length > 0) {
        const contributions = contributionService.getContributionsByType(contributionType);
        for (const childContributionId of childContributionIds) {
            const childContributionProperties = contributions[childContributionId];
            if (childContributionProperties) {
                childContributions.push({
                    description: "",
                    id: childContributionId,
                    properties: childContributionProperties,
                    includes: [],
                    targets: [],
                    type: contributionType
                });
            }
        }
    }

    return childContributions;
}

// exported for testing
export function _modifyContainerTokens(name: string, tokens: MarkdownIt.Token[], idx: number): MarkdownIt.Token[] {
    let iterator: number;
    for (iterator = idx + 1; tokens[idx].level !== tokens[iterator].level || tokens[iterator].type !== "container_" + name + "_close"; iterator++) {
        tokens[iterator].type = MarkdownConstants.MaskedTokenType;
    }
    return tokens;
}
